/**
 * Posts backend client (Beta MVP direct-publish, Feb 2026).
 */
import { api } from '@/lib/api';

export const PostStatus = {
  PUBLISHED: 'published',
  HIDDEN:    'hidden',
  REMOVED:   'removed',
  // Legacy values (still mapped by the backend on read).
  PENDING:   'pending',
  APPROVED:  'approved',
  REJECTED:  'rejected',
};

export const PostKind = {
  PHOTO:        'photo',
  VIDEO:        'video',
  REEL:         'reel',
  BEFORE_AFTER: 'before_after',
  PORTFOLIO:    'portfolio',
};

const isPublicStatus = (s) => s === PostStatus.PUBLISHED || s === PostStatus.APPROVED;

export const postsApi = {
  // Public feed — by default returns only published posts. The backend
  // accepts an explicit `status` ONLY for public-safe values; admins use
  // /posts/moderation/<bucket> for hidden/removed buckets.
  list({ status, author, kind, featured, limit = 50, offset = 0 } = {}) {
    const qs = new URLSearchParams();
    if (status && isPublicStatus(status)) qs.set('status', status);
    if (author) qs.set('author', author);
    if (kind) qs.set('kind', kind);
    if (featured) qs.set('featured', 'true');
    qs.set('limit', String(limit));
    qs.set('offset', String(offset));
    return api.get(`/posts?${qs.toString()}`);
  },
  me({ limit = 50, offset = 0 } = {}) {
    return api.get(`/posts/me?limit=${limit}&offset=${offset}`);
  },
  moderationList(bucket, { limit = 50, offset = 0 } = {}) {
    return api.get(`/posts/moderation/${bucket}?limit=${limit}&offset=${offset}`);
  },
  get(id) { return api.get(`/posts/${id}`); },
  create(payload) { return api.post('/posts', payload); },
  // Iter 106: edit caption/tags/visibility flags. Backend: PATCH /api/posts/:id.
  patch(id, partial) { return api.patch(`/posts/${id}`, partial); },
  // LIMPIEZA ago-2026 — destacar/quitar destacado (staff, flag real).
  setFeatured(id, featured) { return api.patch(`/posts/${id}/feature`, { featured }); },
  remove(id) { return api.delete(`/posts/${id}`); },
  // Moderation
  hide(id, notes)    { return api.post(`/posts/${id}/hide${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`, {}); },
  restore(id, notes) { return api.post(`/posts/${id}/restore${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`, {}); },
  removeAdmin(id, notes) { return api.post(`/posts/${id}/remove${notes ? `?notes=${encodeURIComponent(notes)}` : ''}`, {}); },
  // Likes
  toggleLike(id) { return api.post(`/posts/${id}/like`, {}); },
  myLikeState(id) { return api.get(`/posts/${id}/like`); },
  // Modo Cliente — posts/reels a los que di Me gusta (Feed y Favoritos).
  liked({ kind = 'post', limit = 20, offset = 0 } = {}) {
    return api.get(`/posts/liked?kind=${encodeURIComponent(kind)}&limit=${limit}&offset=${offset}`);
  },
};

/**
 * Reshapes a backend post into the frontend post shape used by PostCard.
 * Resolves relative `/api/uploads/files/...` URLs against the backend host.
 */
const BACKEND_HOST = (process.env.REACT_APP_BACKEND_URL || '').replace(/\/$/, '');
const resolveUrl = (u) => {
  if (!u) return u;
  if (/^https?:/i.test(u)) return u;
  if (u.startsWith('/')) return `${BACKEND_HOST}${u}`;
  return u;
};

export const fromBackend = (be) => ({
  id: be.id,
  __backend: true,
  type: be.kind === 'video' || be.kind === 'reel' || (be.kind === 'review' && be.video_url) ? 'video' : 'photo',
  kind: be.kind,
  // Fase 1 Reseñas — post kind=review enlazado a una reseña (el barbero reseñado ≠ autor).
  review: be.kind === 'review' ? {
    id: be.review_id,
    rating: be.rating,
    displayName: be.display_name,
    verified: Boolean(be.verified_payment),
    serviceName: be.service_name || null,
    barberReply: be.barber_reply || null,
    barber: be.review_barber || { id: be.barber_id },
    barberCanBook: Boolean(be.review_barber_can_book),
  } : null,
  imageUrl: resolveUrl(be.image_url),
  videoUrl: resolveUrl(be.video_url),
  thumbnailUrl: resolveUrl(be.thumbnail_url),
  beforeUrl: resolveUrl(be.before_url),
  afterUrl: resolveUrl(be.after_url),
  caption: be.caption || '',
  tags: be.tags || [],
  likes: be.likes_count || 0,
  // `liked_by_me` is included by the backend when the request carries an
  // auth token. Falls back to false for anonymous viewers. Initial heart
  // state of every PostCard reads this so the icon stays active across
  // refresh / logout / login without an N+1 GET /like burst.
  liked: Boolean(be.liked_by_me),
  // Iter 161 · Scope 1 — Follow chip is now backend-driven. The Feed
  // no longer waits for the local `lineage_follow_graph` cache to
  // hydrate before painting the "Siguiendo" / "Seguir" state. Anon
  // viewers get `false` (harmless — the chip is hidden for own posts
  // and the follow action re-auths anyway).
  is_following: Boolean(be.is_following),
  comments: be.comments_count || 0,
  status: be.status,
  // Iter 206.1 — flag de impulso activo (mayor distribución pagada con Credits).
  boosted: Boolean(be.boosted),
  // LIMPIEZA ago-2026 — destacado por el admin (flag real de servidor).
  featured: Boolean(be.featured),
  barberUsername: be.author_username,
  // Modo Cliente — botón "Agendar" solo si el autor es barbero con servicios activos.
  authorRole: be.author_role || null,
  canBook: Boolean(be.author_can_book),
  likedAt: be.liked_at || null,
  barber: {
    id: be.author_id,
    username: be.author_username,
    name: be.author_name || be.author_username,
    avatar: resolveUrl(be.author_avatar) || `https://api.dicebear.com/7.x/initials/svg?seed=${encodeURIComponent(be.author_username || 'user')}`,
    role: 'barber',
    verified: false,
  },
  timestamp: be.created_at,
  // Iter 106 — owner/admin only: visibility toggles + raw engagement
  // metrics so the IG-style PostOptionsSheet can display stats and
  // mirror toggle state on rerender. Non-owners always see these as
  // false/0 because the backend strips them on read.
  hide_likes:    Boolean(be.hide_likes),
  hide_comments: Boolean(be.hide_comments),
  hide_shares:   Boolean(be.hide_shares),
  hide_saves:    Boolean(be.hide_saves),
  likes_count:    be.likes_count    ?? 0,
  comments_count: be.comments_count ?? 0,
  shares_count:   be.shares_count   ?? 0,
  saves_count:    be.saves_count    ?? 0,
  views_count:    be.views_count    ?? 0,
});

export default postsApi;
